/* eslint-disable react/display-name */
import React, { forwardRef } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Menu, MenuItem, Stack } from '@mui/material'
import moment from 'moment'
import { SoloRangeSlider } from '../shared/date'
import { StyledButton } from '@/shared/ui/buttons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'
import Kedo from '@/Kedo.js'
export interface DateFieldSearchProps {
    kedo: Kedo
    date1: string | null
    date2?: string | null
    searchType: string
    show_slider?: boolean
    onChangeSearch: (searchType: string) => void
    onChangeDate: (
        moment: moment.Moment | null,
        name?: string,
        searchType?: string
    ) => void
    isInvalid: () => boolean
    getErrorText: () => string
}

export const DateFieldSearch = forwardRef<
    HTMLDivElement,
    DateFieldSearchProps
>(
    (
        {
            kedo,
            date1,
            date2,
            searchType,
            show_slider,
            onChangeSearch,
            onChangeDate,
            isInvalid,
            getErrorText,
        },
        ref
    ) => {
        const date1Value = date1 ? moment(date1) : null

        const date2Value = date2 ? moment(date2) : null
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

        const openMenu = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)
        }

        const closeMenu = () => {
            setAnchorEl(null)
        }

        const handleSelectSearchType = (type: string) => {
            onChangeSearch(type)
            closeMenu()
        }

        return (
            <Stack ref={ref} direction={'row'} spacing={1}>
                <StyledButton
                    variant="outlined"
                    invert
                    color="secondary"
                    onClick={openMenu}
                    endIcon={<FontAwesomeIcon icon={faCaretDown} size="sm" />}
                    sx={{
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                        '&:focus': {
                            boxShadow: 'none',
                        },
                        '&:active': {
                            boxShadow: 'none',
                        },
                        '& .MuiButton-endIcon svg ': {
                            fontSize: '16px',
                        },
                    }}
                >
                    {kedo.t(
                        searchType === 'on'
                            ? 'time_on'
                            : searchType === 'after'
                            ? 'time_after'
                            : searchType === 'before'
                            ? 'time_before'
                            : 'time_from_till'
                    )}
                </StyledButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={closeMenu}
                >
                    <MenuItem onClick={() => handleSelectSearchType('on')}>
                        {kedo.t('time_on')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchType('after')}>
                        {kedo.t('time_after')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchType('before')}>
                        {kedo.t('time_before')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchType('between')}>
                        {kedo.t('time_from_till')}
                    </MenuItem>
                </Menu>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    {!show_slider ? (
                        <DatePicker
                            value={date1Value}
                            onChange={(momentValue) => {
                                onChangeDate?.(momentValue, 'date1')
                            }}
                            format="DD-MM-YYYY"
                            views={['year', 'month', 'day']}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                },
                            }}
                        />
                    ) : (
                        <SoloRangeSlider
                            min={18}
                            max={120}
                            type={searchType}
                            onChange={(min, max) => {
                                const tillDate = moment().subtract(min, 'years')
                                const fromDate = moment().subtract(max, 'years')
                                onChangeDate(tillDate, 'date1')
                                onChangeDate(fromDate, 'date2')
                            }}
                        />
                    )}

                    {!show_slider && searchType === 'between' && (
                        <DatePicker
                            value={date2Value}
                            onChange={(momentValue) => {
                                onChangeDate?.(momentValue, 'date2')
                            }}
                            format="DD-MM-YYYY"
                            views={['year', 'month', 'day']}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                },
                            }}
                        />
                    )}
                </LocalizationProvider>

                {isInvalid() ? (
                    <span className="fake-feedback">{getErrorText()}</span>
                ) : null}
            </Stack>
        )
    }
)
